import {ChangeDetectionStrategy, Component, computed, input, model} from '@angular/core';
import {AppNumberPipe} from '@frontend/shared';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  CountryCoverageBand,
  RfpParticipatingTool,
  SuitabilityMode,
  SuitabilityThresholds,
} from '../rfp-invitation.types';
import {countryCoverageBand, coverageFor} from '../rfp-suitability.calc';
import {separateLabels, spreadOverlapping} from './matrix-layout.calc';

const PLOT = {left: 64, top: 24, width: 660, height: 340};
const GRID_STEPS = [0, 20, 40, 60, 80, 100];
const MIN_RADIUS = 9;
const RADIUS_PER_PERCENT = 0.09;
const LABEL_LENGTH = 18;
const LABEL_GAP = 4;
const LABEL_FLIP_SHARE = 0.6;
const LABEL_BASELINE = 4;
const LABEL_LINE_HEIGHT = 14;
const PLOT_BOUNDS = {
  left: PLOT.left,
  top: PLOT.top,
  right: PLOT.left + PLOT.width,
  bottom: PLOT.top + PLOT.height,
};

const TICK_BASELINE = 4;
const SELECTION_RING_WIDTH = 3;

export const COUNTRY_BAND_COLORS: Record<CountryCoverageBand, string> = {
  high: 'var(--color-green)',
  medium: 'var(--color-blue)',
  low: 'var(--color-yellow)',
};

interface MatrixPoint {
  id: string;
  index: number;
  name: string;
  label: string;
  coverage: number;
  rfi: number;
  countryCoverage: number;
  dataCompleteness: number;
  x: number;
  y: number;
  trueX: number;
  trueY: number;
  displaced: boolean;
  radius: number;
  labelX: number;
  labelY: number;
  numberY: number;
  ringColor: string | null;
  ringWidth: number | null;
  labelAnchor: 'start' | 'end';
  color: string;
}

@Component({
  selector: 'app-rfp-suitability-matrix',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AppNumberPipe, TranslocoPipe],
  templateUrl: './rfp-suitability-matrix.component.html',
})
export class RfpSuitabilityMatrixComponent {
  readonly tools = input.required<RfpParticipatingTool[]>();
  readonly mode = input.required<SuitabilityMode>();
  readonly thresholds = input.required<SuitabilityThresholds>();
  readonly selectedToolId = model<string | null>(null);

  protected readonly viewBox = `0 0 ${PLOT.left + PLOT.width + 24} ${PLOT.top + PLOT.height + 48}`;
  protected readonly plotLeft = PLOT.left;
  protected readonly plotRight = PLOT.left + PLOT.width;
  protected readonly plotTop = PLOT.top;
  protected readonly plotBottom = PLOT.top + PLOT.height;
  protected readonly tickBaseline = TICK_BASELINE;
  protected readonly yTickX = PLOT.left - 10;
  protected readonly xTickY = PLOT.top + PLOT.height + 18;
  protected readonly gridLines = GRID_STEPS.map((step) => ({
    step,
    x: this.xPosition(step),
    y: this.yPosition(step),
  }));
  protected readonly xAxisLabel = {x: PLOT.left + PLOT.width / 2, y: PLOT.top + PLOT.height + 40};
  protected readonly yAxisLabel = {
    x: 16,
    y: PLOT.top + PLOT.height / 2,
    transform: `rotate(-90 16 ${PLOT.top + PLOT.height / 2})`,
  };

  protected readonly points = computed((): MatrixPoint[] => {
    const placed = this.tools().map((tool) => {
      const dataCompleteness = tool.suitability?.dataCompleteness ?? 0;
      return {
        tool,
        coverage: coverageFor(tool, this.mode()),
        rfi: tool.rfiPercentage ?? 0,
        dataCompleteness,
        radius: MIN_RADIUS + dataCompleteness * RADIUS_PER_PERCENT,
      };
    });
    const truePositions = placed.map((point) => ({
      x: this.xPosition(point.coverage),
      y: this.yPosition(point.rfi),
      radius: point.radius,
    }));
    const positions = spreadOverlapping(truePositions, PLOT_BOUNDS);
    const labels = positions.map((position, index) => {
      const onTheLeft = position.x > PLOT.left + PLOT.width * LABEL_FLIP_SHARE;
      const offset = placed[index].radius + LABEL_GAP;
      return {
        x: onTheLeft ? position.x - offset : position.x + offset,
        y: position.y + LABEL_BASELINE,
        anchor: onTheLeft ? ('end' as const) : ('start' as const),
      };
    });
    const labelYs = separateLabels(labels, LABEL_LINE_HEIGHT);

    return placed.map((point, index) => {
      const name = point.tool.translatedName ?? point.tool.name;
      const countryCoverage = point.tool.suitability?.countryCoverage ?? 0;
      const position = positions[index];
      const truePosition = truePositions[index];
      return {
        id: point.tool.id,
        index: index + 1,
        name,
        label: name.length > LABEL_LENGTH ? `${name.slice(0, LABEL_LENGTH - 1)}…` : name,
        coverage: point.coverage,
        rfi: point.rfi,
        countryCoverage,
        dataCompleteness: point.dataCompleteness,
        x: position.x,
        y: position.y,
        trueX: truePosition.x,
        trueY: truePosition.y,
        displaced: position.x !== truePosition.x || position.y !== truePosition.y,
        radius: point.radius,
        labelX: labels[index].x,
        labelY: labelYs[index],
        labelAnchor: labels[index].anchor,
        numberY: position.y + TICK_BASELINE,
        ringColor: point.tool.id === this.selectedToolId() ? 'var(--color-white)' : null,
        ringWidth: point.tool.id === this.selectedToolId() ? SELECTION_RING_WIDTH : null,
        color: COUNTRY_BAND_COLORS[countryCoverageBand(countryCoverage)],
      };
    });
  });
  protected readonly thresholdPositions = computed(() => ({
    x: this.xPosition(this.thresholds().coverage),
    y: this.yPosition(this.thresholds().rfi),
  }));
  protected readonly preferredArea = computed(() => {
    const threshold = this.thresholdPositions();
    return {
      x: threshold.x,
      y: PLOT.top,
      width: PLOT.left + PLOT.width - threshold.x,
      height: threshold.y - PLOT.top,
    };
  });
  protected readonly xAxisLabelKey = computed(() =>
    this.mode() === 'combined'
      ? 'project.steps.rfp.invitationList.matrix.axisCombined'
      : 'project.steps.rfp.invitationList.matrix.axisFunctions',
  );

  protected xPosition(percent: number): number {
    return PLOT.left + (this.clamp(percent) / 100) * PLOT.width;
  }

  protected yPosition(percent: number): number {
    return PLOT.top + ((100 - this.clamp(percent)) / 100) * PLOT.height;
  }

  protected select(toolId: string): void {
    this.selectedToolId.set(toolId);
  }

  protected selectByKey(event: Event, toolId: string): void {
    event.preventDefault();
    this.select(toolId);
  }

  private clamp(percent: number): number {
    return Math.min(100, Math.max(0, percent));
  }
}
